import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, ClipboardList, Clock, RefreshCw, XCircle } from 'lucide-react';
import api from '../utils/axios';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import Dropdown from '../components/Dropdown';
import SearchableSelect from '../components/SearchableSelect';
import DocumentSubsection from '../components/DocumentSubsection';
import { COUNTRY_OPTIONS } from '../constants/countries';
import { companyToday } from '../utils/dateUtils';
import useLatestRequest from '../hooks/useLatestRequest';

const OnboardingForm = () => {
  const { user, refreshProfile } = useAuth();
  const navigate = useNavigate();
  const [existingRequest, setExistingRequest] = useState(undefined); // undefined = loading
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showNewForm, setShowNewForm] = useState(false);
  const todayStr = companyToday();

  // Email is no longer captured here — both work and personal emails are
  // provisioned at user creation (POST /api/users) and are surfaced read-only
  // on the form via the user info card.
  const emptyForm = {
    first_name: '', last_name: '',
    phone: '', cnic: '', date_of_birth: '', gender: '',
    address: '', city: '', country: '',
    emergency_contact_name: '', emergency_contact_phone: '', emergency_contact_relationship: '',
  };
  const [form, setForm] = useState(emptyForm);
  const [documents, setDocuments] = useState([]);

  // Keyed on the employee id, not the user object: refreshProfile below hands back a new
  // user object even when nothing changed, which would re-run the effect and refetch.
  const employeeId = user?.employee_id;
  const profileRefreshed = useRef(false);
  // The re-read below failed (a 5xx, the network, the rate limiter), so the page cannot
  // tell a linked account from an unlinked one and must not claim either.
  const [profileReadFailed, setProfileReadFailed] = useState(false);
  // Only the latest load may pre-fill the form: StrictMode runs the effect below twice in
  // dev, Try again starts another, and a late response would overwrite what the user has
  // typed since.
  const beginRequest = useLatestRequest();

  const loadRequest = useCallback(async () => {
    const isCurrent = beginRequest();
    setLoading(true);
    setProfileReadFailed(false);
    try {
      const res = await api.get('/api/onboarding/me');
      if (!isCurrent()) return;
      const req = res.data.data;
      // The user's employee_id alone decides "onboarded", because it is what ProtectedRoute
      // gates on. An approved request on a user the client still sees as unlinked means a
      // profile from before HR approved, or an employee record deleted since. Sending them
      // to '/' bounced off ProtectedRoute straight back here, refetching on every bounce.
      // Re-read the profile instead, once: if it is linked now, the effect below takes
      // them to '/' (so the spinner stays up); if not, the page says so and asks the
      // server nothing more.
      if (req?.status === 'approved' && !profileRefreshed.current) {
        profileRefreshed.current = true;
        const fresh = await refreshProfile();
        if (!isCurrent() || fresh?.employee_id) return;
        setProfileReadFailed(!fresh);
      }
      setExistingRequest(req);
      if (req?.status === 'pending_review') {
        // Pre-fill form
        setForm({
          first_name: req.first_name || '', last_name: req.last_name || '',
          phone: req.phone || '',
          cnic: req.cnic || '', date_of_birth: req.date_of_birth || '',
          gender: req.gender || '', address: req.address || '',
          city: req.city || '', country: req.country || '',
          emergency_contact_name: req.emergency_contact_name || '',
          emergency_contact_phone: req.emergency_contact_phone || '',
          emergency_contact_relationship: req.emergency_contact_relationship || '',
        });
        if (Array.isArray(req.documents_data)) {
          setDocuments(req.documents_data.map((d, i) => ({ ...d, _id: d._id || `existing_${i}` })));
        }
      }
    } catch {
      // Nothing to resume, or nothing the form can read (a malformed document entry):
      // it opens as a new request rather than spinning forever.
      if (!isCurrent()) return;
      setExistingRequest(null);
    }
    setLoading(false);
  }, [beginRequest, refreshProfile]);

  useEffect(() => {
    // If user already has employee_id, redirect to dashboard
    if (employeeId) { navigate('/'); return; }
    loadRequest();
  }, [employeeId, navigate, loadRequest]);

  // The one way back from a failed re-read, and only on a click: nothing here retries by
  // itself, so a rate-limited user is not kept rate-limited by this page.
  const retryProfileRead = () => {
    profileRefreshed.current = false;
    loadRequest();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.date_of_birth && form.date_of_birth > todayStr) {
      toast.error('Date of birth cannot be in the future');
      return;
    }
    const cleanedDocs = documents
      .filter(d => d.document_name?.trim() && d.document_type?.trim() && d.google_drive_link?.trim())
      .map(({ _id, ...rest }) => rest);
    const invalidDoc = documents.find(d =>
      (d.document_name?.trim() || d.document_type?.trim() || d.google_drive_link?.trim()) &&
      !(d.document_name?.trim() && d.document_type?.trim() && d.google_drive_link?.trim())
    );
    if (invalidDoc) {
      toast.error('Each document needs a type, name, and Google Drive link');
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form, documents_data: cleanedDocs };
      if (existingRequest?.status === 'pending_review' && !showNewForm) {
        await api.put(`/api/onboarding/${existingRequest.id}`, payload);
        toast.success('Onboarding information updated');
      } else {
        await api.post('/api/onboarding', payload);
        toast.success('Onboarding request submitted!');
      }
      const res = await api.get('/api/onboarding/me');
      setExistingRequest(res.data.data);
      setShowNewForm(false);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  const set = (field) => (e) => setForm(prev => ({ ...prev, [field]: e.target.value }));

  const formatCNIC = (value) => {
    let clean = value.replace(/[^0-9]/g, '');
    if (clean.length > 13) clean = clean.slice(0, 13);
    let formatted = clean;
    if (clean.length > 5) formatted = clean.slice(0, 5) + '-' + clean.slice(5);
    if (clean.length > 12) formatted = formatted.slice(0, 13) + '-' + formatted.slice(13);
    return formatted;
  };

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600" />
      </div>
    );
  }

  // Approved, but the re-read profile never arrived. The account may well be linked by
  // now, so this must not send them to HR; it offers the re-read again instead.
  if (existingRequest?.status === 'approved' && profileReadFailed) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="bg-white border border-gray-200 rounded-lg p-8 text-center">
          <RefreshCw className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">We couldn't confirm your account just now</h2>
          <p className="text-gray-600 text-sm">
            Your onboarding request was approved, but we couldn't check whether your account is ready to use. This is usually temporary, so please wait a moment and try again.
          </p>
          <button
            type="button"
            onClick={retryProfileRead}
            className="mt-6 px-4 py-2 bg-primary-600 text-white rounded-md text-sm font-medium hover:bg-primary-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  // Approved, and the re-read profile still names no employee record. Nothing on this
  // page can fix that, so it says who can and leaves the server alone.
  if (existingRequest?.status === 'approved') {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="bg-warning-50 border border-warning-200 rounded-lg p-8 text-center">
          <AlertTriangle className="mx-auto h-12 w-12 text-warning-500 mb-4" />
          <h2 className="text-xl font-semibold text-warning-900 mb-2">Approved, but your account is not linked yet</h2>
          <p className="text-warning-700 text-sm">
            HR approved your onboarding request, but your account still has to be connected to your employee record before you can use the portal. Please contact HR to finish setting it up.
          </p>
        </div>
      </div>
    );
  }

  // In Review state
  if (existingRequest?.status === 'in_review' && !showNewForm) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-8 text-center">
          <Clock className="mx-auto h-12 w-12 text-blue-500 mb-4" />
          <h2 className="text-xl font-semibold text-blue-900 mb-2">Your request is under review</h2>
          <p className="text-blue-700 text-sm">HR is reviewing your submission and will contact you shortly.</p>
        </div>
      </div>
    );
  }

  // Rejected state
  if (existingRequest?.status === 'rejected' && !showNewForm) {
    return (
      <div className="max-w-2xl mx-auto space-y-4">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex items-center space-x-2 mb-2">
            <XCircle className="h-5 w-5 text-red-500" />
            <h2 className="text-lg font-semibold text-red-900">Request Rejected</h2>
          </div>
          {existingRequest.rejection_reason && (
            <p className="text-sm text-red-700 mb-1">
              <span className="font-medium">Reason:</span> {existingRequest.rejection_reason}
            </p>
          )}
          {existingRequest.review_comments && (
            <p className="text-sm text-red-600">{existingRequest.review_comments}</p>
          )}
        </div>
        <button
          onClick={() => { setShowNewForm(true); setForm({ ...emptyForm }); setDocuments([]); }}
          className="px-4 py-2 bg-primary-600 text-white rounded-md text-sm hover:bg-primary-700"
        >
          Submit New Request
        </button>
      </div>
    );
  }

  // Form (new or editing pending_review)
  const isEditing = existingRequest?.status === 'pending_review' && !showNewForm;
  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div className="flex items-center space-x-3">
        <ClipboardList className="h-7 w-7 text-primary-600" />
        <h1 className="text-2xl font-bold text-gray-900">
          {isEditing ? 'Update Onboarding Request' : 'Complete Your Onboarding'}
        </h1>
      </div>
      <p className="text-sm text-gray-500">
        Fill in your personal information. HR will review and add your employment details.
      </p>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Personal Info */}
        <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-4">
          <h2 className="font-semibold text-gray-800">Personal Information</h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                First Name <span className="text-red-500">*</span>
              </label>
              <input
                required
                value={form.first_name}
                onChange={set('first_name')}
                data-testid="onboarding-field-first-name"
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Last Name <span className="text-red-500">*</span>
              </label>
              <input
                required
                value={form.last_name}
                onChange={set('last_name')}
                data-testid="onboarding-field-last-name"
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>
          <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm">
            <div className="font-medium text-gray-700 mb-1">Your contact emails</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-gray-600">
              <div>
                <span className="text-gray-500">Work email: </span>
                <span className="font-medium text-gray-900">{user?.email || '—'}</span>
              </div>
              <div>
                <span className="text-gray-500">Personal email: </span>
                <span className="font-medium text-gray-900">{user?.personal_email || '—'}</span>
              </div>
            </div>
            <p className="mt-1 text-xs text-gray-500">
              These were set up by HR when your account was created. To change your personal email, use the Profile page.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
              <input
                value={form.phone}
                onChange={set('phone')}
                data-testid="onboarding-field-phone"
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                CNIC <span className="text-xs text-gray-400 font-normal">(Format: XXXXX-XXXXXXX-X)</span>
              </label>
              <input
                value={form.cnic}
                onChange={e => setForm(prev => ({ ...prev, cnic: formatCNIC(e.target.value) }))}
                placeholder="XXXXX-XXXXXXX-X"
                data-testid="onboarding-field-cnic"
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth</label>
              <input
                type="date"
                value={form.date_of_birth}
                onChange={set('date_of_birth')}
                max={todayStr}
                data-testid="onboarding-field-date-of-birth"
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Gender</label>
              <Dropdown
                value={form.gender}
                onChange={set('gender')}
                data-testid="onboarding-field-gender"
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">Select</option>
                <option>Male</option>
                <option>Female</option>
                <option>Other</option>
              </Dropdown>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
            <textarea
              value={form.address}
              onChange={set('address')}
              rows={2}
              data-testid="onboarding-field-address"
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
              <input
                value={form.city}
                onChange={set('city')}
                data-testid="onboarding-field-city"
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
              <SearchableSelect
                value={form.country}
                onChange={(val) => setForm(prev => ({ ...prev, country: val }))}
                options={COUNTRY_OPTIONS}
                placeholder="Select country..."
                data-testid="onboarding-field-country"
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>
        </div>

        {/* Emergency Contact */}
        <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-4">
          <h2 className="font-semibold text-gray-800">Emergency Contact</h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
              <input
                value={form.emergency_contact_name}
                onChange={set('emergency_contact_name')}
                data-testid="onboarding-field-emergency-contact-name"
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
              <input
                value={form.emergency_contact_phone}
                onChange={set('emergency_contact_phone')}
                data-testid="onboarding-field-emergency-contact-phone"
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Relationship</label>
            <input
              value={form.emergency_contact_relationship}
              onChange={set('emergency_contact_relationship')}
              data-testid="onboarding-field-emergency-contact-relationship"
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
        </div>

        {/* Documents */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <p className="text-xs text-gray-500 mb-3">
            Upload your documents to Google Drive (or any shareable link) and paste the links below. HR will review them during onboarding.
          </p>
          <DocumentSubsection documents={documents} onChange={setDocuments} />
        </div>

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={submitting}
            data-testid="onboarding-submit"
            className="px-6 py-2 bg-primary-600 text-white text-sm font-medium rounded-md hover:bg-primary-700 disabled:opacity-50"
          >
            {submitting ? 'Submitting...' : isEditing ? 'Update Request' : 'Submit Request'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default OnboardingForm;
